import { useEffect, useState } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";
import "../../assets/css/Contact/ContactsDrawer.css";
import LeadsDynamic from "../Leads/LeadsDynamic";
import DealsDynamic from "../Deals/DealsDynamic";
import {
  MdBusiness,
  MdLanguage,
  MdLocationOn,
  MdClose,
  MdEmail,
  MdPhone,
} from "react-icons/md";
import { FaEdit, FaTrashAlt, FaExternalLinkAlt } from "react-icons/fa";

export default function ContactDrawer({
  contact,
  isOpen,
  onClose,
  refreshContacts,
}) {
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    id: null,
  });

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [contactDetails, setContactDetails] = useState(contact || {});

  const validateForm = () => {
    const newErrors = {};

    // First Name
    if (!form.first_name.trim()) {
      newErrors.first_name = "First name is required";
    } else if (form.first_name.trim().length > 60) {
      newErrors.first_name = "First name cannot exceed 60 characters";
    }

    // Last Name
    if (!form.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    } else if (form.last_name.trim().length > 60) {
      newErrors.last_name = "Last name cannot exceed 60 characters";
    }

    // Email
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    ) {
      newErrors.email = "Enter a valid email address";
    }

    // Phone
    const trimmedPhone = form.phone ? form.phone.trim() : "";
    const phoneDigits = trimmedPhone.replace(/\D/g, "");

    if (!trimmedPhone) {
      newErrors.phone = "Phone number is required";
    } else if (
      trimmedPhone.length > 18 ||
      !/^\+?[0-9\s-]+$/.test(trimmedPhone) ||
      phoneDigits.length < 6 ||
      phoneDigits.length > 15
    ) {
      newErrors.phone = "Phone number must be valid (up to 18 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sync API data -> form
  useEffect(() => {
    if (!contact) return;

    setContactDetails(contact);
    setForm({
      id: contact.id,
      organization_id: contact.organization_id || null,
      first_name: contact.first_name || "",
      last_name: contact.second_name || contact.last_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
    });

    setEditMode(false);

    const fetchFullContact = async () => {
      try {
        const res = await api.get(`/contact/get/${contact.id}`);
        if (res?.data) {
          setContactDetails((prev) => ({
            ...prev,
            ...res.data,
            deals: res.data.deals ?? prev?.deals ?? [],
            leads: res.data.leads ?? prev?.leads ?? [],
          }));
        }
      } catch (err) {
        console.error("Failed loading full contact details", err);
      }
    };

    if (contact.id) {
      fetchFullContact();
    }
  }, [contact?.id]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const getInitials = (first = "", last = "") => {
    const f = (first || "").trim().charAt(0).toUpperCase();
    const l = (last || "").trim().charAt(0).toUpperCase();
    return `${f}${l}`.trim() || "C";
  };

  // Role permissions
  const currentUser = JSON.parse(sessionStorage.getItem("user"));
  const permissions = {
    super_admin: { edit: true, delete: true },
    sales_manager: { edit: true, delete: false },
    sales_executive: { edit: false, delete: false },
  };

  const role = currentUser?.role
    ?.toLowerCase()
    ?.trim()
    ?.replace(/\s+/g, "_");

  const permission = permissions?.[role] ?? {
    edit: false,
    delete: false,
  };

  const fullName = `${form.first_name} ${form.last_name}`.trim();

  // UPDATE
  const updateContact = async () => {
    if (!validateForm()) return;

    try {
      await api.put(`/contact/update/${contact.id}`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        organization_id: form.organization_id || null,
      });

      await Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        text: "Contact details have been saved.",
        timer: 1500,
        showConfirmButton: false,
      });

      refreshContacts();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Something went wrong while updating the contact.",
      });
    }
  };

  // DELETE
  const deleteContact = async () => {
    const res = await Swal.fire({
      title: "Delete contact?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
    });

    if (!res.isConfirmed) return;

    try {
      await api.delete(`/contact/delete/${contact.id}`);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1500,
        showConfirmButton: false,
      });

      refreshContacts();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
      });
    }
  };

  const orgsList = contactDetails?.organizations || contact?.organizations || [];

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div
        className="contact-drawer organization-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="drawer-header-c drawer-header-o">
          <div className="contact-header-left organization-header-left">
            <div className="contact-header-info organization-header-info">
              <div className="contact-main-avatar">
                {getInitials(form.first_name, form.last_name)}
              </div>

              <div className="contact-title-block organization-title-block">
                <h2>{fullName || "No Name"}</h2>
                <div className="contact-badges organization-badges">
                  {(contactDetails?.designation || contact?.designation) && (
                    <span className="designation-badge">
                      {contactDetails.designation || contact.designation}
                    </span>
                  )}
                  <span className="contact-role active">Contact</span>
                </div>
              </div>
            </div>
          </div>

          <div className="header-right">
            {!editMode && (
              <div className="header-icon-actions">
                {permission.edit && (
                  <button
                    aria-label="edit-contact"
                    className="icon-btn edit-btn"
                    onClick={() => setEditMode(true)}
                    title="Edit Contact"
                  >
                    <FaEdit />
                  </button>
                )}

                {permission.delete && (
                  <button
                    aria-label="delete-contact"
                    className="icon-btn delete-btn"
                    onClick={deleteContact}
                    title="Delete Contact"
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
            )}

            <button
              className="close-btn"
              onClick={onClose}
              title="Close Drawer"
            >
              <MdClose />
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="drawer-tabs">
          <button
            className={activeTab === "overview" ? "active-tab" : ""}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>

          <button
            className={activeTab === "leads" ? "active-tab" : ""}
            onClick={() => setActiveTab("leads")}
          >
            Leads
          </button>

          <button
            className={activeTab === "deals" ? "active-tab" : ""}
            onClick={() => setActiveTab("deals")}
          >
            Deals
          </button>
        </div>

        {/* ================= OVERVIEW TAB ================= */}
        {activeTab === "overview" && (
          <div className="drawer-overview-container">
            {/* DETAILS (Clean, open, borderless) */}
            <div className="drawer-content">
              <div className="detail-grid">
                <div>
                  <label htmlFor="first_name">First Name</label>
                  {editMode ? (
                    <>
                      <input
                        id="first_name"
                        name="first_name"
                        value={form.first_name}
                        onChange={handleChange}
                        className={errors.first_name ? "input-error error" : ""}
                        placeholder="First Name"
                      />
                      {errors.first_name && (
                        <span className="error-text">{errors.first_name}</span>
                      )}
                    </>
                  ) : (
                    <p>{form.first_name || "-"}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last_name">Last Name</label>
                  {editMode ? (
                    <>
                      <input
                        id="last_name"
                        name="last_name"
                        value={form.last_name}
                        onChange={handleChange}
                        className={errors.last_name ? "input-error error" : ""}
                        placeholder="Last Name"
                      />
                      {errors.last_name && (
                        <span className="error-text">{errors.last_name}</span>
                      )}
                    </>
                  ) : (
                    <p>{form.last_name || "-"}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email">Email</label>
                  {editMode ? (
                    <>
                      <input
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={errors.email ? "input-error error" : ""}
                        placeholder="name@example.com"
                      />
                      {errors.email && (
                        <span className="error-text">{errors.email}</span>
                      )}
                    </>
                  ) : (
                    <p>{form.email || "-"}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone">Phone</label>
                  {editMode ? (
                    <>
                      <input
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        maxLength={18}
                        className={errors.phone ? "input-error error" : ""}
                        placeholder="e.g. +91 - 8887924662"
                      />
                      {errors.phone && (
                        <span className="error-text">{errors.phone}</span>
                      )}
                    </>
                  ) : (
                    <p>{form.phone || "-"}</p>
                  )}
                </div>

                <div>
                  <label>Created By</label>
                  <p>{contact?.created_by?.name || contact?.created_by || "-"}</p>
                </div>
              </div>

              {editMode && (
                <div className="overview-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setForm({
                        id: contact.id,
                        organization_id: contact.organization_id || null,
                        first_name: contact.first_name || "",
                        last_name: contact.second_name || contact.last_name || "",
                        email: contact.email || "",
                        phone: contact.phone || "",
                      });
                      setErrors({
                        first_name: "",
                        last_name: "",
                        email: "",
                        phone: "",
                      });
                      setEditMode(false);
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn-primary"
                    onClick={updateContact}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* ORGANIZATIONS SECTION */}
            <div className="drawer-organizations-wrap">
              <div className="drawer-section-heading">
                <h4>
                  <div className="section-icon-wrap">
                    <MdBusiness />
                  </div>
                  Organizations ({orgsList.length})
                </h4>
                <span className="count-chip">{orgsList.length} Connected</span>
              </div>

              {orgsList.length === 0 ? (
                <p className="empty-org-text">No organizations found.</p>
              ) : (
                <div className="contacts-list org-assoc-list">
                  {orgsList.map((org) => (
                    <div className="contact-card org-assoc-card" key={org.id}>
                      <div className="contact-avatar org-assoc-avatar">
                        {org.name?.charAt(0)?.toUpperCase() || "O"}
                      </div>

                      <div className="contact-info org-assoc-info">
                        <div className="org-assoc-title-row">
                          <h4>{org.name}</h4>
                          {org.status && (
                            <span
                              className={`contact-role status-pill ${
                                org.status.toLowerCase() === "active"
                                  ? "active"
                                  : "inactive"
                              }`}
                            >
                              <span className="status-dot"></span>
                              {org.status}
                            </span>
                          )}
                        </div>

                        <div className="org-assoc-meta">
                          {org.website ? (
                            <a
                              href={
                                org.website.startsWith("http")
                                  ? org.website
                                  : `https://${org.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="org-assoc-link"
                            >
                              <MdLanguage className="org-assoc-icon" />
                              <span>{org.website}</span>
                              <FaExternalLinkAlt size={9} className="external-icon" />
                            </a>
                          ) : (
                            <p className="org-assoc-item">
                              <MdLanguage className="org-assoc-icon" />
                              <span>-</span>
                            </p>
                          )}

                          <p className="org-assoc-item">
                            <MdLocationOn className="org-assoc-icon" />
                            <span>
                              {[org.country, org.state]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================= LEADS TAB ================= */}
        {activeTab === "leads" && (
          <div className="drawer-dynamic-tab-container">
            <LeadsDynamic
              leads={
                (contactDetails?.leads || contact?.leads || []).map((lead) => ({
                  ...lead,
                  organization_name:
                    lead.organization?.name ||
                    lead.organization_name ||
                    orgsList[0]?.name ||
                    "",
                  contact_name: `${contactDetails?.first_name || contact?.first_name || ""} ${contactDetails?.second_name || contact?.second_name || contactDetails?.last_name || ""}`.trim(),
                  contact_email: contactDetails?.email || contact?.email,
                  contact_phone: contactDetails?.phone || contact?.phone,
                }))
              }
              title="Leads"
            />
          </div>
        )}

        {/* ================= DEALS TAB ================= */}
        {activeTab === "deals" && (
          <div className="drawer-dynamic-tab-container">
            <DealsDynamic
              deals={
                (contactDetails?.deals || contact?.deals || []).map((deal) => ({
                  ...deal,
                  organization_name:
                    deal.organization?.name ||
                    deal.organization_name ||
                    orgsList[0]?.name ||
                    "",
                  contact_name: `${contactDetails?.first_name || contact?.first_name || ""} ${contactDetails?.second_name || contact?.second_name || contactDetails?.last_name || ""}`.trim(),
                  contact_email: contactDetails?.email || contact?.email,
                  contact_phone: contactDetails?.phone || contact?.phone,
                  description: deal.description || deal.lead?.description || "",
                }))
              }
              title="Deals"
              onDealClick={(deal) => {
                console.log("Selected Deal:", deal);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}